/**
 * `HistogramChart` — design §4 step 9's "distribution plot" over `conditions`' `by_unit[]`
 * shares. Hand-built SVG in `BaselineCharts.tsx`'s register (the repo forbids chart
 * libraries, plan Global Constraints): viewBox-scaled, one horizontal bar per row, a value
 * label beside each bar rather than an axis a reader has to read off.
 */
import { useRef } from 'react';

import { fmtNum } from '../../../lib/format';
import { AxisLabels } from '../../../components/charts/AxisLabels';
import { useAxisTitles } from '../../../components/charts/axisContext';
import { axisBands } from '../../../components/charts/scale';
import { RowLabel } from '../../../components/charts/RowLabel';
import { LABEL_LINE_H } from '../../../components/charts/labels';
import { useChartMarks } from '../../../components/charts/useChartMarks';
import { ROW_LABEL_PX, rowLabelColumn, valueWidth } from './labels';
import { useIntelWidth } from './layout';

/** The width the chart is laid out at where its box cannot be measured (#749: it measures). */
export const HG_W = 320;
export const HG_ROW_H = 22;
export const HG_ROW_GAP = 10;
/** The label column's minimum; it grows to the longest name (cap 45 %). */
export const HG_LABEL_W = 110;
export const HG_VALUE_W = 52;
export const HG_PAD_TOP = 8;
export const HG_MIN_H = 60;

export interface HistogramRow {
  id: string;
  label: string;
  /** A share, 0–100; `null` draws no bar and prints an em dash. */
  value: number | null;
}

export interface HistogramChartProps {
  rows: HistogramRow[];
  title: string;
  color?: string;
  selectedId?: string | null;
  onSelect?: (id: string) => void;
  /** The x axis' title, "quantity (unit)", centred under the chart (launch round). */
  xLabel?: string;
  /** The y axis' title, rotated at the left of the label column. */
  yLabel?: string;
  /** Layout width in px; measured from the chart's own box when omitted (#749). */
  w?: number;
}

/** The chart's own `aria-label` — a plain-text readout of every row, for §I5 and the test list. */
function readout(title: string, rows: HistogramRow[]): string {
  const parts = rows.map((r) => `${r.label}: ${r.value == null ? '-' : `${fmtNum(r.value, 1)}%`}`);
  return `${title}: ${parts.join(', ')}`;
}

export function HistogramChart({
  rows,
  title,
  color = 'var(--brand)',
  selectedId,
  onSelect,
  xLabel: xLabelProp,
  yLabel: yLabelProp,
  w: wProp,
}: HistogramChartProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const { xLabel, yLabel } = useAxisTitles(xLabelProp, yLabelProp);
  const bands = axisBands(xLabel, yLabel);
  const outerW = useIntelWidth(svgRef, wProp, HG_W + bands.left);
  const W = outerW - bands.left;
  const marks = useChartMarks<string>();
  const texts = rows.map((r) => (r.value == null ? '-' : `${fmtNum(r.value, 1)}%`));
  const column = rowLabelColumn(
    rows.map((r) => r.label),
    W,
    { min: HG_LABEL_W },
  );
  const plotX = column.width;
  const valueW = Math.max(HG_VALUE_W, Math.ceil(Math.max(0, ...texts.map((t) => valueWidth(t))) + 10));
  const plotW = Math.max(20, W - plotX - valueW);
  const extras = column.lines.map((l) => Math.max(0, l.length * ROW_LABEL_PX * LABEL_LINE_H + 4 - HG_ROW_H));
  const tops = extras.map(
    (_, i) => HG_PAD_TOP + i * (HG_ROW_H + HG_ROW_GAP) + extras.slice(0, i).reduce((a, b) => a + b, 0),
  );
  const h = Math.max(
    HG_MIN_H,
    HG_PAD_TOP + Math.max(rows.length, 1) * (HG_ROW_H + HG_ROW_GAP) + extras.reduce((a, b) => a + b, 0),
  );
  return (
    <svg
      ref={svgRef}
      className={`intel-chart hg-chart${marks.rootClass}`}
      width="100%"
      viewBox={`0 0 ${outerW} ${h + bands.bottom}`}
      role="img"
      aria-label={readout(title, rows)}
      data-testid="histogram-chart"
      data-fluid=""
      data-w={outerW}
      onMouseLeave={marks.clear}
    >
      <AxisLabels
        xLabel={xLabel}
        yLabel={yLabel}
        plotLeft={bands.left + plotX}
        plotRight={bands.left + plotX + plotW}
        plotTop={HG_PAD_TOP}
        plotBottom={h}
        height={h + bands.bottom}
      />
      <g transform={bands.left ? `translate(${bands.left} 0)` : undefined} role="list">
      {rows.map((row, i) => {
        const y = tops[i]! + extras[i]! / 2;
        const w = row.value == null ? 0 : Math.max(2, (Math.min(row.value, 100) / 100) * plotW);
        const text = texts[i]!;
        const selected = row.id === selectedId;
        return (
          <g
            key={row.id}
            className={`hg-row mark${marks.isHot(row.id) ? ' is-hot' : ''}`}
            data-unit={row.id}
            {...marks.markProps(
              row.id,
              () => ({ label: row.label, value: text }),
              onSelect ? () => onSelect(row.id) : undefined,
            )}
          >
            <title>{`${row.label} · ${text}`}</title>
            <RowLabel
              className="hg-rowlabel"
              lines={column.lines[i] ?? [row.label]}
              x={plotX - 8}
              baseline={y + HG_ROW_H / 2 + 4}
              size={ROW_LABEL_PX}
              textAnchor="end"
            />
            {w > 0 ? (
              <rect
                className="mk"
                x={plotX}
                y={y}
                width={w}
                height={HG_ROW_H}
                rx={3}
                fill={color}
                opacity={selected ? 1 : 0.78}
                stroke={selected ? 'var(--ink)' : 'none'}
                strokeWidth={selected ? 1.5 : 0}
              />
            ) : null}
            <text className="hg-num" x={plotX + w + 6} y={y + HG_ROW_H / 2 + 4}>
              {text}
            </text>
          </g>
        );
      })}
      </g>
    </svg>
  );
}

export default HistogramChart;
